/* MCR · Style match (<mcr-style-match>) — a free, rule-based stylist for the product page.
   Builds a complete look per occasion from: hand-picked metafield products → style:<occasion> tags →
   Search & Discovery complementary products → collection pools ranked by colour pairing + keywords.
   Colours come from color:<name> tags, variant/title words or, as a last resort, the product photo
   (read in the browser on a tiny canvas). Everything runs client-side; nothing leaves the store. */
(function () {
  'use strict';
  if (customElements.get('mcr-style-match')) return;

  var TONE_KEY = 'mcr:tone:v1';
  var cache = {};

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }
  function money(cents, fmt) {
    var p = ((cents || 0) / 100).toFixed(2).split('.');
    function g(n, sep) { return n.replace(/\B(?=(\d{3})+(?!\d))/g, sep); }
    return String(fmt || '₹{{amount}}').replace(/<[^>]*>/g, '').replace(/\{\{\s*(\w+)\s*\}\}/, function (m, k) {
      if (k === 'amount_no_decimals') return g(p[0], ',');
      if (k === 'amount_with_comma_separator') return g(p[0], '.') + ',' + p[1];
      if (k === 'amount_no_decimals_with_comma_separator') return g(p[0], '.');
      return g(p[0], ',') + '.' + p[1];
    });
  }
  function words(s) {
    return String(s || '').toLowerCase().split(',').map(function (w) { return w.trim(); }).filter(Boolean);
  }
  function textOf(p) {
    return ((p.t || '') + ' ' + (p.ty || '') + ' ' + (p.tg || []).join(' ')).toLowerCase();
  }
  function hasWord(text, w) {
    var i = text.indexOf(w);
    while (i > -1) {
      var a = text.charAt(i - 1), b = text.charAt(i + w.length);
      if (!/[a-z]/.test(a) && !/[a-z]/.test(b)) return true;
      i = text.indexOf(w, i + 1);
    }
    return false;
  }

  /* ---------- colour table ---------- */
  function hexToLab(hex) {
    hex = hex.replace('#', '');
    if (hex.length === 3) hex = hex.replace(/./g, '$&$&');
    var n = parseInt(hex, 16);
    return rgbToLab((n >> 16) & 255, (n >> 8) & 255, n & 255);
  }
  function rgbToLab(r, g, b) {
    function lin(c) { c /= 255; return c > 0.04045 ? Math.pow((c + 0.055) / 1.055, 2.4) : c / 12.92; }
    var R = lin(r), G = lin(g), B = lin(b);
    var X = (R * 0.4124 + G * 0.3576 + B * 0.1805) / 0.95047;
    var Y = R * 0.2126 + G * 0.7152 + B * 0.0722;
    var Z = (R * 0.0193 + G * 0.1192 + B * 0.9505) / 1.08883;
    function f(t) { return t > 0.008856 ? Math.cbrt(t) : 7.787 * t + 16 / 116; }
    return [116 * f(Y) - 16, 500 * (f(X) - f(Y)), 200 * (f(Y) - f(Z))];
  }
  function dist(a, b) { var l = a[0] - b[0], x = a[1] - b[1], y = a[2] - b[2]; return l * l + x * x + y * y; }

  function parseColours(text) {
    var list = [];
    String(text || '').split(/\r?\n/).forEach(function (line) {
      var c = line.split('|').map(function (x) { return x.trim(); });
      if (!c[0] || !/^#[0-9a-f]{3,6}$/i.test(c[1] || '')) return;
      var key = c[0].toLowerCase();
      var aliases = words(c[2]); if (aliases.indexOf(key) < 0) aliases.push(key);
      list.push({ key: key, name: c[0], hex: c[1], lab: hexToLab(c[1]), aliases: aliases, pairs: words(c[3]) });
    });
    return list;
  }

  /* ---------- product classification ---------- */
  function kind(p) {
    var ty = (p.ty || '').toLowerCase(), t = (p.t || '').toLowerCase(), all = textOf(p);
    if (/\bbelt\b/.test(t) && !/combo|set/.test(ty)) return 'belt';
    if (/kerchief|hanky/.test(t)) return 'kerchief';
    if (/towel|innerwear|vest|brief|trunk/.test(ty) || /towel|brief|trunk/.test(t)) return 'other';
    if (ty === 'set' || /\bset\b/.test(t) && /shirt|dhot/.test(t)) return 'set';
    if (/t-shirt|t shirt|tshirt/.test(ty + ' ' + t)) return 'tee';
    if (/shirt/.test(ty) || (/shirt/.test(t) && !/dhot/.test(t))) return 'shirt';
    if (/dhot|lungi|mundu|veshti/.test(all)) return 'dhoti';
    return 'other';
  }
  function isSingle(p) {
    var t = textOf(p);
    return /\bw\/s\b|\bc\/s\b|single|2\.00 ?mtr|1\.90 ?mtr|2 ?mtr/.test(t);
  }

  /* ---------- data loading (Section Rendering API → sections/mcr-style-data.liquid) ---------- */
  function load(url) {
    if (!url) return Promise.resolve([]);
    if (!cache[url]) {
      cache[url] = fetch(url, { credentials: 'same-origin' })
        .then(function (r) { return r.ok ? r.text() : ''; })
        .then(function (html) {
          var m = html.match(/<script type="application\/json" data-mcr-style-data>([\s\S]*?)<\/script>/);
          if (!m) return [];
          try { return JSON.parse(m[1]); } catch (e) { return []; }
        })
        .catch(function () { return []; });
    }
    return cache[url];
  }

  /* ---------- colour from the photo (tiny canvas, centre of the garment) ---------- */
  var photoCache = {};
  function photoColour(url, palette) {
    if (!url) return Promise.resolve(null);
    if (photoCache[url]) return photoCache[url];
    photoCache[url] = new Promise(function (resolve) {
      var img = new Image();
      img.crossOrigin = 'anonymous';
      img.decoding = 'async';
      img.onload = function () {
        try {
          var W = 40, H = 50, c = document.createElement('canvas');
          c.width = W; c.height = H;
          var ctx = c.getContext('2d', { willReadFrequently: true });
          ctx.drawImage(img, 0, 0, W, H);
          var d = ctx.getImageData(0, 0, W, H).data;
          function px(x, y) { var i = (y * W + x) * 4; return [d[i], d[i + 1], d[i + 2]]; }
          var corners = [px(1, 1), px(W - 2, 1), px(1, H - 2), px(W - 2, H - 2)];
          var bg = [0, 1, 2].map(function (k) { return corners.reduce(function (s, c2) { return s + c2[k]; }, 0) / 4; });
          var votes = {};
          for (var y = Math.floor(H * 0.34); y < Math.floor(H * 0.62); y++) {
            for (var x = Math.floor(W * 0.38); x < Math.floor(W * 0.62); x++) {
              var p = px(x, y);
              var db = Math.abs(p[0] - bg[0]) + Math.abs(p[1] - bg[1]) + Math.abs(p[2] - bg[2]);
              if (db < 30) continue; // background
              var lab = rgbToLab(p[0], p[1], p[2]);
              var best = null, bd = Infinity;
              palette.forEach(function (col) { var dd = dist(lab, col.lab); if (dd < bd) { bd = dd; best = col.key; } });
              votes[best] = (votes[best] || 0) + 1;
            }
          }
          var top = null, n = 0;
          Object.keys(votes).forEach(function (k) { if (votes[k] > n) { n = votes[k]; top = k; } });
          resolve(n >= 12 ? top : null);
        } catch (e) { resolve(null); }
      };
      img.onerror = function () { resolve(null); };
      img.src = url.replace(/([?&])width=\d+/, '$1width=160');
    });
    return photoCache[url];
  }

  class McrStyleMatch extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      this._init = true;
      try { this.cfg = JSON.parse(this.querySelector('[data-sm-config]').textContent); } catch (e) { return; }
      var self = this, cfg = this.cfg;
      this.palette = parseColours(cfg.colours);
      this.stage = this.querySelector('[data-sm-stage]');
      this.cur = cfg.current;
      this.curKind = kind(this.cur);
      this.curVariant = cfg.variant;
      this.picked = {};    // occasion id → {slot: index}
      this.seed = {};      // occasion id → shuffle seed
      this.off = {};       // item key → excluded from bag
      this.tone = null;
      try { this.tone = JSON.parse(sessionStorage.getItem(TONE_KEY) || 'null'); } catch (e) {}

      var tabs = this.querySelectorAll('[data-sm-tab]');
      var first = this.querySelector('[data-sm-tab][aria-selected="true"]') || tabs[0];
      this.active = first ? first.dataset.smTab : null;

      this.addEventListener('click', function (e) {
        var tab = e.target.closest('[data-sm-tab]');
        if (tab) { self.select(tab.dataset.smTab); return; }
        var sw = e.target.closest('[data-sm-swap]');
        if (sw) { self.swap(sw.dataset.slot, +sw.dataset.smSwap); return; }
        if (e.target.closest('[data-sm-shuffle]')) { self.shuffle(); return; }
        if (e.target.closest('[data-sm-add]')) { self.add(); return; }
        if (e.target.closest('[data-sm-share]')) { self.share(); return; }
        if (e.target.closest('[data-sm-tone-clear]')) { self.setTone(null); return; }
        var ask = e.target.closest('[data-sm-ask]');
        if (ask) {
          var bot = document.querySelector('mcr-assist');
          if (bot && typeof bot.toggle === 'function' && typeof bot.ask === 'function') {
            e.preventDefault();
            bot.toggle(true);
            bot.ask(ask.dataset.smAsk);
          }
        }
      });
      this.addEventListener('change', function (e) {
        var inc = e.target.closest('[data-sm-include]');
        if (inc) { self.off[inc.dataset.smInclude] = !inc.checked; self.totals(); return; }
        var vs = e.target.closest('[data-sm-variant]');
        if (vs) { self.totals(); return; }
        if (e.target.matches('[data-sm-colour-select]')) {
          self.curColour = e.target.value || null;
          self.userColour = !!self.curColour;
          self.picked = {};
          self.colourUi();
          self.render();
        }
      });
      this.querySelector('[data-sm-tabs]').addEventListener('keydown', function (e) {
        if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
        var list = Array.prototype.slice.call(tabs);
        var i = list.indexOf(document.activeElement);
        if (i < 0) return;
        var n = list[(i + (e.key === 'ArrowRight' ? 1 : list.length - 1)) % list.length];
        n.focus(); self.select(n.dataset.smTab);
      });

      // stay in sync with the main variant picker
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        subscribe(PUB_SUB_EVENTS.variantChange, function (payload) {
          var v = payload && payload.data && payload.data.variant;
          if (!v) return;
          self.curVariant = v.id;
          var row = (self.cur.v || []).filter(function (x) { return x[0] === v.id; })[0];
          if (row) { row[2] = v.price; row[3] = v.available; }
          self.totals();
        });
      }
      // skin tone from the selfie colour match (MCR · Help & urgent block)
      document.addEventListener('mcr:tone', function (e) { self.setTone(e.detail || null); });
      var toneBtn = this.querySelector('[data-sm-tone]');
      if (toneBtn && document.querySelector('[data-mcr-match]')) toneBtn.hidden = false;

      var start = function () { self.boot(); };
      if ('IntersectionObserver' in window) {
        var io = new IntersectionObserver(function (en) {
          if (!en[0].isIntersecting) return;
          io.disconnect(); start();
        }, { rootMargin: '0px 0px 600px 0px' });
        io.observe(this);
      } else start();
    }

    /* ---------- boot: work out the colour of the product on screen ---------- */
    boot() {
      var self = this;
      this.curColour = this.colourOf(this.cur);
      var ready = this.curColour || this.curKind === 'belt' || this.curKind === 'kerchief'
        ? Promise.resolve(this.curColour)
        : photoColour(this.cur.i, this.palette).then(function (c) { self.curColour = c; self.photoGuess = !!c; return c; });
      ready.then(function () { self.colourUi(); self.render(); });
    }

    colourByKey(k) { return this.palette.filter(function (c) { return c.key === k; })[0] || null; }

    colourOf(p) {
      var pre = this.cfg.colorPrefix, self = this;
      var tag = (p.tg || []).map(function (t) { return t.toLowerCase(); }).filter(function (t) { return t.indexOf(pre) === 0; })[0];
      if (tag) {
        var k = tag.slice(pre.length).trim();
        var hit = this.palette.filter(function (c) { return c.key === k || c.aliases.indexOf(k) > -1; })[0];
        if (hit) return hit.key;
        var near = this.findColour(k, true);
        if (near) return near;
      }
      var ci = (p.o || []).map(function (o) { return String(o).toLowerCase(); }).indexOf('color');
      if (ci > -1 && p.v && p.v[0]) {
        var vt = String(p.v[0][1]).split(' / ')[ci];
        var fromOpt = this.findColour(String(vt || '').toLowerCase(), false);
        if (fromOpt) return fromOpt;
      }
      var t = (p.t || '').toLowerCase();
      var k2 = kind(p);
      if (k2 === 'dhoti') {
        // "... W/D DHOTI 3.80 MTR - Maroon" → the border colour sits after the last dash
        var tail = t.split(/\s-\s|-(?=[a-z ]+$)/).pop();
        return this.findColour(tail, true) || this.findColour(t, true);
      }
      return this.findColour(t, false);
    }

    findColour(text, preferColour) {
      var found = [];
      this.palette.forEach(function (c) {
        c.aliases.forEach(function (a) { if (hasWord(text, a)) found.push({ key: c.key, len: a.length }); });
      });
      if (!found.length) return null;
      if (preferColour) {
        var colourful = found.filter(function (f) { return f.key !== 'white' && f.key !== 'cream'; });
        if (colourful.length) found = colourful;
      }
      found.sort(function (a, b) { return b.len - a.len; });
      return found[0].key;
    }

    colourUi() {
      var wrap = this.querySelector('[data-sm-colour]');
      if (!wrap) return;
      var sel = wrap.querySelector('[data-sm-colour-select]');
      var dot = wrap.querySelector('[data-sm-colour-dot]');
      if (!sel.options.length) {
        sel.innerHTML = '<option value="">—</option>' + this.palette.map(function (c) {
          return '<option value="' + esc(c.key) + '">' + esc(c.name) + '</option>';
        }).join('');
      }
      sel.value = this.curColour || '';
      var c = this.colourByKey(this.curColour);
      dot.style.background = c ? c.hex : 'transparent';
      dot.classList.toggle('is-empty', !c);
      wrap.hidden = !this.colourMatters();
    }

    colourMatters() { return this.curKind !== 'belt' && this.curKind !== 'kerchief' && this.curKind !== 'set'; }

    /* ---------- skin tone ---------- */
    setTone(t) {
      this.tone = t && t.tone ? t : null;
      try {
        if (this.tone) sessionStorage.setItem(TONE_KEY, JSON.stringify(this.tone));
        else sessionStorage.removeItem(TONE_KEY);
      } catch (e) {}
      this.picked = {};
      if (this.booted) this.render();
    }
    toneColours() {
      if (!this.tone || !this.tone.colors) return [];
      var self = this, out = [];
      this.tone.colors.forEach(function (n) {
        var k = self.findColour(String(n).toLowerCase(), false);
        if (k && out.indexOf(k) < 0) out.push(k);
      });
      return out;
    }

    /* ---------- occasions ---------- */
    occasion(id) {
      if (id === '__pick') return { id: '__pick', pick: true, note: this.cfg.pickNote, title: '' };
      return this.cfg.occasions.filter(function (o) { return o.id === id; })[0] || this.cfg.occasions[0];
    }
    select(id) {
      this.active = id;
      this.querySelectorAll('[data-sm-tab]').forEach(function (b) {
        var on = b.dataset.smTab === id;
        b.setAttribute('aria-selected', on ? 'true' : 'false');
        b.tabIndex = on ? 0 : -1;
      });
      this.render();
    }
    slotsFor(occ) {
      var k = this.curKind, s = [];
      if (k === 'shirt') s = ['dhoti', 'belt', 'kerchief'];
      else if (k === 'tee') s = ['dhoti', 'kerchief'];
      else if (k === 'dhoti') s = ['shirt', 'belt', 'kerchief'];
      else if (k === 'set') s = ['belt', 'kerchief'];
      else s = ['shirt', 'dhoti'];
      if (!occ.belt) s = s.filter(function (x) { return x !== 'belt' || k === 'set'; });
      if (!occ.kerchief) s = s.filter(function (x) { return x !== 'kerchief' || k === 'set'; });
      return s;
    }
    poolUrls(slot, occ) {
      var root = (this.cfg.root || '/').replace(/\/?$/, '/'), P = this.cfg.pools, h = [];
      function col(handle) { return handle ? root + 'collections/' + handle + '?section_id=mcr-style-data' : null; }
      if (slot === 'shirt') h.push(col(occ.shirt || P.shirt));
      if (slot === 'dhoti') {
        if (occ.dhoti) h.push(col(occ.dhoti));
        else if ((occ.len === 'single' || this.curKind === 'tee') && P.single) h.push(col(P.single));
        else h.push(col(P.dhoti));
      }
      if (slot === 'belt') h.push(col(P.belt));
      if (slot === 'kerchief') h.push(col(P.kerchief));
      if (slot === 'shirt' || slot === 'dhoti') {
        // products tagged style:<occasion> anywhere in the catalogue
        var tagHandle = (this.cfg.stylePrefix + occ.key).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
        h.push(root + 'collections/all/' + tagHandle + '?section_id=mcr-style-data');
        // colour-targeted catalogue search, so matches aren't limited to the first page of a collection
        var self = this, cur = this.colourByKey(this.curColour), want = [];
        var neutral = function (k) { return k === 'white' || k === 'cream' || k === 'gold'; };
        var pairs = cur ? cur.pairs.filter(function (k) { return !neutral(k); }) : [];
        if (occ.mode === 'tonal' && cur) want = [cur.key].concat(pairs.slice(0, 1));
        else if (occ.mode === 'contrast') want = pairs.slice(0, 2);
        else if (this.userColour && cur) want = [cur.key].concat(pairs.slice(0, 1));
        this.toneColours().slice(0, 1).forEach(function (k) { if (want.indexOf(k) < 0) want.push(k); });
        want.forEach(function (k) {
          var c = self.colourByKey(k);
          if (!c) return;
          h.push(root + 'search?type=product&options%5Bunavailable_products%5D=hide&q=' + encodeURIComponent(c.name.toLowerCase() + ' ' + slot) + '&section_id=mcr-style-data');
        });
      }
      return h.filter(Boolean);
    }

    /* ---------- scoring ---------- */
    score(p, slot, occ, ctx) {
      var t = textOf(p), s = 0, why = null, self = this;
      var cur = this.curColour, pairs = (this.colourByKey(cur) || { pairs: [] }).pairs;
      var pc = p._c;
      if (ctx.comp[p.id]) { s += 26; why = 'often'; }
      var styleTag = this.cfg.stylePrefix + occ.key;
      if ((p.tg || []).some(function (x) { return x.toLowerCase() === styleTag; })) { s += 45; why = 'tag'; }
      var kwHits = 0;
      words(occ.kw).forEach(function (w) { if (kwHits < 2 && hasWord(t, w)) { kwHits++; s += 12; } });
      var kwWhy = kwHits ? 'kw' : null;
      words(occ.avoid).forEach(function (w) { if (hasWord(t, w)) s -= 30; });

      if (slot === 'dhoti' && occ.len !== 'any' && this.curKind !== 'tee') s += (isSingle(p) === (occ.len === 'single')) ? 10 : -14;
      if (slot === 'dhoti' && this.curKind === 'tee') s += isSingle(p) ? 10 : -10;

      if (slot === 'belt') {
        var cream = ctx.cream;
        var bc = /cream/.test(t) ? 'cream' : 'white';
        if (bc === (cream ? 'cream' : 'white')) { s += 24; why = why || 'belt'; }
        if (/pocket|poket/.test(t)) s += 3;
      } else if (slot === 'kerchief') {
        var white = /white/.test(t) && !/colour|color|assorted/.test(t);
        if (occ.mode === 'classic' ? white : !white) { s += 18; why = why || 'kerchief'; }
      } else if (pc) {
        var neutral = pc === 'white' || pc === 'cream' || pc === 'gold';
        if (occ.mode === 'classic' && slot === 'dhoti' && !neutral && (pc === cur || pairs.indexOf(pc) > -1)) {
          s += 34; why = why || (pc === cur ? 'echo' : 'pair');
        } else if (occ.mode === 'classic') {
          if (neutral) { s += slot === 'dhoti' && cur ? 22 : 30; why = why || 'classic'; }
          else if (pairs.indexOf(pc) > -1) { s += 8; }
          else s -= 6;
        } else if (occ.mode === 'tonal') {
          if (pc === cur) { s += 30; why = why || 'echo'; }
          else if (neutral) { s += pairs.indexOf(pc) > -1 ? 18 : 10; why = why || 'classic'; }
          else if (pairs.indexOf(pc) > -1) { s += 22; why = why || 'pair'; }
        } else {
          if (pc !== cur && pairs.indexOf(pc) > -1 && !neutral) { s += 30; why = why || 'pair'; }
          else if (pc === cur) s -= 10;
          else if (neutral) { s += 8; why = why || 'classic'; }
        }
        // a colour the shopper picked themselves leads the look
        if (this.userColour && !neutral) {
          if (pc === cur) { s += 50; why = 'echo'; }
          else if (pairs.indexOf(pc) > -1) { s += 24; why = why === 'classic' || !why ? 'pair' : why; }
        }
        if (ctx.tone.indexOf(pc) > -1) { s += 14; why = why === 'classic' || !why ? 'tone' : why; }
      }
      if (!why) why = kwWhy;
      // stable per-look variety; a shuffle changes the seed
      var h = 0, key = String(p.id) + ':' + ctx.seed;
      for (var i = 0; i < key.length; i++) h = (h * 31 + key.charCodeAt(i)) >>> 0;
      s += (h % 100) / (ctx.seed ? 7 : 25);
      return { s: s, why: why };
    }

    whyText(why, p) {
      var c = this.colourByKey(p._c), cur = this.colourByKey(this.curColour);
      var cn = c ? c.name : '', curn = cur ? cur.name.toLowerCase() : '';
      switch (why) {
        case 'tag': return 'Stylist pick for ' + (this.occasion(this.active).title || 'this look');
        case 'often': return 'Often worn together';
        case 'echo': return cn + ' echoes your ' + (curn || 'piece');
        case 'pair': return cn + (curn ? ' lifts ' + curn : ' pairs well');
        case 'classic': return (cn || 'Classic') + ' — always right';
        case 'tone': return cn + ' suits your skin tone';
        case 'kw': return 'Made for the occasion';
        case 'belt': return 'Matches the dhoti';
        case 'kerchief': return 'Finishing touch';
        default: return '';
      }
    }

    /* ---------- build a look ---------- */
    build(occ) {
      var self = this;
      if (occ.pick) {
        var slots = (this.cfg.picks || []).filter(function (p) { return p.a && p.id !== self.cur.id; }).map(function (p) {
          p._c = p._c || self.colourOf(p);
          return { slot: 'pick-' + p.id, kind: kind(p), list: [{ p: p, why: 'tag' }] };
        });
        return Promise.resolve(slots);
      }
      var slotNames = this.slotsFor(occ);
      var compP = this.cfg.recoUrl ? load(this.cfg.recoUrl) : Promise.resolve([]);
      var pools = slotNames.map(function (sl) {
        return Promise.all(self.poolUrls(sl, occ).map(load)).then(function (lists) { return [].concat.apply([], lists); });
      });
      return Promise.all([compP].concat(pools)).then(function (res) {
        var comp = res[0] || [], compIds = {};
        comp.forEach(function (p) { compIds[p.id] = 1; });
        var ctx = { comp: compIds, seed: self.seed[occ.id] || 0, tone: self.toneColours(), cream: /cream|\bc\/d\b|\bc\/s\b/.test(textOf(self.cur)) };
        var need = [];
        var built = slotNames.map(function (sl, i) {
          var seen = {}, list = [];
          comp.concat(res[i + 1] || []).forEach(function (p) {
            if (seen[p.id] || p.id === self.cur.id || !p.a || /combo|buy 1|\(1\+1\)|pack of/i.test(p.t)) return;
            var k = kind(p);
            if (sl === 'dhoti' ? k !== 'dhoti' : sl === 'shirt' ? k !== 'shirt' : k !== sl) return;
            seen[p.id] = 1;
            if (p._c === undefined) p._c = self.colourOf(p);
            if (!p._c && (sl === 'shirt') && p.i) need.push(p);
            list.push(p);
          });
          return { slot: sl, kind: sl, raw: list };
        });
        // read shirt colours from photos when titles don't name them (only a small, bounded batch)
        var batch = need.slice(0, 24).map(function (p) {
          return photoColour(p.i, self.palette).then(function (c) { p._c = c || null; });
        });
        return Promise.all(batch).then(function () {
          built.forEach(function (b) {
            if (b.slot === 'dhoti') {
              var d = b.raw.map(function (p) { var r = self.score(p, b.slot, occ, ctx); return { p: p, s: r.s, why: r.why }; });
              d.sort(function (a, z) { return z.s - a.s; });
              b.list = d.slice(0, self.cfg.alts);
              // a cream dhoti asks for a cream belt
              var top = b.list[0] && b.list[0].p;
              if (top) ctx.cream = ctx.cream || /cream|\bc\/d\b/.test(textOf(top));
            }
          });
          built.forEach(function (b) {
            if (b.list) return;
            var r2 = b.raw.map(function (p) { var r = self.score(p, b.slot, occ, ctx); return { p: p, s: r.s, why: r.why }; });
            r2.sort(function (a, z) { return z.s - a.s; });
            b.list = r2.slice(0, self.cfg.alts);
          });
          return built.filter(function (b) { return b.list.length; });
        });
      });
    }

    render() {
      var self = this, occ = this.occasion(this.active);
      if (!occ) return;
      this.booted = true;
      var token = (this._token = (this._token || 0) + 1);
      this.stage.querySelector('.mcr-sm__board') && this.stage.querySelector('.mcr-sm__board').classList.add('is-busy');
      this.build(occ).then(function (slots) {
        if (token !== self._token) return;
        self.slots = slots;
        self.paint(occ);
      });
    }

    label(slot) {
      var L = this.cfg.labels;
      return L[slot] || L.item;
    }

    variantSelect(p, key) {
      var v = (p.v || []).filter(function (x) { return x[3]; });
      if (!v.length) return '';
      if (v.length === 1 && /default title/i.test(v[0][1])) {
        return '<input type="hidden" data-sm-variant="' + esc(key) + '" value="' + v[0][0] + '" data-price="' + v[0][2] + '">';
      }
      return '<label class="mcr-sm__select"><span class="visually-hidden">' + esc(p.t) + '</span><select data-sm-variant="' + esc(key) + '">' +
        v.map(function (x) { return '<option value="' + x[0] + '" data-price="' + x[2] + '">' + esc(x[1]) + ' · ' + esc(money(x[2], this.cfg.moneyFormat)) + '</option>'; }, this).join('') +
        '</select></label>';
    }

    paint(occ) {
      var self = this, L = this.cfg.labels, fmt = this.cfg.moneyFormat;
      var picked = this.picked[occ.id] || (this.picked[occ.id] = {});
      if (!this.slots.length) {
        this.stage.innerHTML = '<p class="mcr-sm__empty">' + esc(L.empty) + '</p>';
        return;
      }
      var cur = this.cur, cc = this.colourMatters() ? this.colourByKey(this.curColour) : null;
      var curVar = (cur.v || []).filter(function (x) { return x[0] === self.curVariant; })[0] || (cur.v || [])[0] || [0, '', cur.p, cur.a];
      var html = '<div class="mcr-sm__board" data-occ="' + esc(occ.id) + '">';
      html += '<div class="mcr-sm__pieces">';
      html += '<article class="mcr-sm__card mcr-sm__card--hero' + (this.off.__cur ? ' is-off' : '') + '">' +
        '<div class="mcr-sm__card-top"><span class="mcr-sm__slot">' + esc(L.piece) + '</span>' +
        (cc ? '<span class="mcr-sm__chipc"><i style="background:' + esc(cc.hex) + '"></i>' + esc(cc.name) + '</span>' : '') + '</div>' +
        '<div class="mcr-sm__media" data-mcr-fit>' + (cur.i ? '<img src="' + esc(cur.i) + '" alt="' + esc(cur.t) + '" loading="lazy" width="400" height="500">' : '') + '</div>' +
        '<p class="mcr-sm__title">' + esc(cur.t) + '</p>' +
        '<p class="mcr-sm__price" data-sm-cur-price>' + esc(money(curVar[2], fmt)) + '</p>' +
        '<label class="mcr-sm__inc"><input type="checkbox" data-sm-include="__cur"' + (this.off.__cur ? '' : ' checked') + '><span>' + esc(L.include) + '</span></label>' +
        '</article>';

      this.slots.forEach(function (b) {
        var idx = picked[b.slot] || 0;
        if (idx >= b.list.length) idx = 0;
        var it = b.list[idx], p = it.p, key = b.slot + ':' + p.id;
        var why = self.whyText(it.why, p);
        var pc = self.colourByKey(p._c);
        var many = b.list.length > 1;
        html += '<article class="mcr-sm__card' + (self.off[b.slot] ? ' is-off' : '') + '" data-slot="' + esc(b.slot) + '" data-key="' + esc(key) + '">' +
          '<div class="mcr-sm__card-top"><span class="mcr-sm__slot">' + esc(self.label(b.kind)) + '</span>' +
          (many ? '<span class="mcr-sm__swap">' +
            '<button type="button" data-sm-swap="-1" data-slot="' + esc(b.slot) + '" aria-label="' + esc(L.prev) + '">‹</button>' +
            '<em>' + (idx + 1) + '/' + b.list.length + '</em>' +
            '<button type="button" data-sm-swap="1" data-slot="' + esc(b.slot) + '" aria-label="' + esc(L.next) + '">›</button></span>' : '') +
          '</div>' +
          '<a class="mcr-sm__media" data-mcr-fit href="' + esc(p.u) + '">' + (p.i ? '<img src="' + esc(p.i) + '" alt="' + esc(p.t) + '" loading="lazy" width="400" height="500">' : '') +
          (why ? '<span class="mcr-sm__why">' + (pc ? '<i style="background:' + esc(pc.hex) + '"></i>' : '') + esc(why) + '</span>' : '') + '</a>' +
          '<p class="mcr-sm__title"><a href="' + esc(p.u) + '">' + esc(p.t) + '</a></p>' +
          '<p class="mcr-sm__price" data-sm-price>' + esc(money(p.p, fmt)) + '</p>' +
          self.variantSelect(p, b.slot) +
          '<label class="mcr-sm__inc"><input type="checkbox" data-sm-include="' + esc(b.slot) + '"' + (self.off[b.slot] ? '' : ' checked') + '><span>' + esc(L.include) + '</span></label>' +
          '</article>';
      });
      html += '</div>';

      var toneC = this.tone ? '<p class="mcr-sm__toneon">' + esc((L.toneOn || '').replace('[tone]', this.tone.label || this.tone.tone)) +
        ' <button type="button" data-sm-tone-clear aria-label="Clear">×</button></p>' : '';
      html += '<aside class="mcr-sm__sum">' +
        (occ.note ? '<p class="mcr-sm__note">' + esc(occ.note) + '</p>' : '') + toneC +
        '<div class="mcr-sm__total"><span>' + esc(L.total) + '<small data-sm-count></small></span><strong data-sm-total></strong></div>' +
        '<button type="button" class="mcr-sm__add" data-sm-add><span data-sm-add-text>' + esc(L.add) + '</span></button>' +
        '<p class="mcr-sm__msg" data-sm-msg role="status" hidden></p>' +
        '<div class="mcr-sm__links">' +
        (occ.pick ? '' : '<button type="button" class="mcr-sm__link" data-sm-shuffle>↻ ' + esc(L.shuffle) + '</button>') +
        '<button type="button" class="mcr-sm__link" data-sm-share>' + esc(L.share) + '</button></div>' +
        '</aside></div>';
      this.stage.innerHTML = html;
      this.totals();
    }

    swap(slot, dir) {
      var occ = this.occasion(this.active);
      var b = (this.slots || []).filter(function (x) { return x.slot === slot; })[0];
      if (!b) return;
      var picked = this.picked[occ.id] || (this.picked[occ.id] = {});
      picked[slot] = ((picked[slot] || 0) + dir + b.list.length) % b.list.length;
      var focus = dir > 0 ? '[data-sm-swap="1"]' : '[data-sm-swap="-1"]';
      this.paint(occ);
      var btn = this.stage.querySelector('[data-slot="' + slot + '"] ' + focus);
      if (btn) btn.focus();
    }
    shuffle() {
      var occ = this.occasion(this.active);
      this.seed[occ.id] = (this.seed[occ.id] || 0) + 1;
      this.picked[occ.id] = {};
      this.render();
    }

    items() {
      var self = this, out = [];
      if (!this.off.__cur && this.curVariant) {
        var cv = (this.cur.v || []).filter(function (x) { return x[0] === self.curVariant; })[0];
        if (!cv || cv[3]) out.push({ id: this.curVariant, price: cv ? cv[2] : this.cur.p, title: this.cur.t, url: this.cur.u });
      }
      this.stage.querySelectorAll('.mcr-sm__card[data-slot]').forEach(function (card) {
        var slot = card.dataset.slot;
        if (self.off[slot]) return;
        var inp = card.querySelector('[data-sm-variant]');
        if (!inp) return;
        var price = inp.tagName === 'SELECT' ? inp.options[inp.selectedIndex].dataset.price : inp.dataset.price;
        var a = card.querySelector('.mcr-sm__title a');
        out.push({ id: +inp.value, price: +price, title: a ? a.textContent : '', url: a ? a.getAttribute('href') : '' });
      });
      return out;
    }

    totals() {
      var self = this, fmt = this.cfg.moneyFormat, L = this.cfg.labels;
      this.stage.querySelectorAll('.mcr-sm__card').forEach(function (card) {
        var inc = card.querySelector('[data-sm-include]');
        card.classList.toggle('is-off', inc && !inc.checked);
        var sel = card.querySelector('select[data-sm-variant]');
        var pr = card.querySelector('[data-sm-price]');
        if (sel && pr) pr.textContent = money(+sel.options[sel.selectedIndex].dataset.price, fmt);
      });
      var cv = (this.cur.v || []).filter(function (x) { return x[0] === self.curVariant; })[0];
      var cp = this.stage.querySelector('[data-sm-cur-price]');
      if (cp && cv) cp.textContent = money(cv[2], fmt);
      var items = this.items();
      var sum = items.reduce(function (s, i) { return s + (i.price || 0); }, 0);
      var t = this.stage.querySelector('[data-sm-total]');
      var c = this.stage.querySelector('[data-sm-count]');
      var btn = this.stage.querySelector('[data-sm-add]');
      if (t) t.textContent = money(sum, fmt);
      if (c) c.textContent = (L.count || '[count]').replace('[count]', items.length);
      if (btn) btn.disabled = !items.length;
    }

    add() {
      var self = this, items = this.items();
      var btn = this.stage.querySelector('[data-sm-add]'), msg = this.stage.querySelector('[data-sm-msg]');
      if (!items.length || !btn) return;
      var drawer = document.querySelector('cart-drawer');
      var body = { items: items.map(function (i) { return { id: i.id, quantity: 1 }; }) };
      if (drawer && drawer.getSectionsToRender) {
        body.sections = drawer.getSectionsToRender().map(function (s) { return s.id; });
        body.sections_url = window.location.pathname;
      }
      btn.disabled = true; btn.classList.add('is-loading'); msg.hidden = true;
      fetch((this.cfg.root || '/').replace(/\/?$/, '/') + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body)
      })
        .then(function (r) { return r.json(); })
        .then(function (res) {
          if (res.status) throw new Error(res.description || res.message || 'Error');
          msg.textContent = self.cfg.labels.added; msg.classList.remove('is-error'); msg.hidden = false;
          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') publish(PUB_SUB_EVENTS.cartUpdate, { source: 'mcr-style-match', cartData: res });
          if (drawer && res.sections) { drawer.classList.remove('is-empty'); drawer.renderContents(res); }
          else window.location = (self.cfg.root || '/').replace(/\/?$/, '/') + 'cart';
        })
        .catch(function (e) { msg.textContent = e.message; msg.classList.add('is-error'); msg.hidden = false; })
        .finally(function () { btn.classList.remove('is-loading'); btn.disabled = false; });
    }

    share() {
      var occ = this.occasion(this.active);
      var origin = window.location.origin;
      var lines = this.items().map(function (i) { return '• ' + i.title + (i.url ? ' — ' + origin + i.url.split('?')[0] : ''); });
      var head = 'My MCR look' + (occ.title ? ' for ' + occ.title : '') + ':';
      window.open('https://wa.me/?text=' + encodeURIComponent(head + '\n' + lines.join('\n')), '_blank', 'noopener');
    }
  }

  customElements.define('mcr-style-match', McrStyleMatch);
})();
